<script>
import variables from '@/styles/_varsJs.scss'
import * as constants from '@/utilities/appConsts'
import {
  CUSTOM_DATE_PICKER_OPTIONS,
  FIXED_DATE_PICKER_OPTIONS,
  LONG_DATE_FORMAT,
} from '@/utilities/appConsts'
import { daysDifference, getDateGivenOffset } from '@/utilities/dates'
import moment from 'moment'

export default {
  props: {
    id: { type: String, default: undefined },
    value: { type: [Object, String, Date, Array], default: () => null },
    label: { type: String, default: '' },
    error: { type: Boolean, default: false },
    rules: { type: [Object, Array], default: undefined },
    autofocus: { type: Boolean, default: false },
    placeholder: { type: String, default: '' },
    format: { type: String, default: () => LONG_DATE_FORMAT },
    required: { type: Boolean, default: false },
    hasIcon: { type: Boolean, default: true },
    iconPosition: {
      type: String,
      default: 'left',
      validator: iconPosition => ['left', 'right'].includes(iconPosition),
    },
    icon: { type: String, default: 'mdi-calendar-blank' },
    iconSize: { type: Number, default: 16 },
    iconAction: { type: Boolean, default: true },
    hideDetails: { type: Boolean, default: false },
    type: {
      type: String,
      default: 'date',
      validator: type => ['date', 'month'].includes(type),
    },
    clearable: { type: Boolean, default: false },
    dropDown: { type: Boolean, default: false },
    color: { type: String, default: undefined },
    max: { type: [String, Date], default: undefined },
    min: { type: [String, Date], default: undefined },
    maxDays: { type: Number, default: 0 },
    rangeOnly: { type: Boolean, default: false },
    multiple: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    reactive: { type: Boolean, default: false },
    scrollable: { type: Boolean, default: false },
    attach: { type: [String, Boolean], default: true },
    top: { type: Boolean, default: false },
    bottom: { type: Boolean, default: false },
    left: { type: Boolean, default: false },
    right: { type: Boolean, default: false },
    tile: { type: Boolean, default: false },
    flat: { type: Boolean, default: true },
    allowedDates: { type: Function, default: null },
    showAdjacentMonths: { type: Boolean, default: false },
    showCurrent: { type: [Boolean, String], default: true },
    showWeek: { type: Boolean, default: false },
    // Refer app.constants.js
    // for FIXED_DATE_PICKER_OPTIONS and CUSTOM_DATE_PICKER_OPTIONS for option ID's
    hideOptionsWithId: { type: Array, default: undefined },
    showFixedOptions: { type: Boolean, default: false },
    defaultOptionId: { type: Number, default: undefined },
    fixedOptions: {
      type: [Array],
      default: () => FIXED_DATE_PICKER_OPTIONS,
    },
    customOptions: {
      type: [Array],
      default: () => CUSTOM_DATE_PICKER_OPTIONS,
    },
    landscape: { type: Boolean, default: false },
    allowOverflow: { type: Boolean, default: false },
    fixed: { type: Boolean, default: false },
    offsetOverflow: { type: Boolean, default: false },
    contentClass: { type: String, default: undefined },
    zIndex: { type: [String, Number], default: undefined },
    openOnClick: { type: Boolean, default: true },
    openOnFocus: { type: Boolean, default: false },
    openOnHover: { type: Boolean, default: false },
    closeOnContentClick: { type: Boolean, default: false },
    autoRePosition: { type: Boolean, default: false },
    floatFilter: { type: Boolean, default: false },
    allowFutureDates: { type: Boolean, default: false },
    transition: { type: String, default: 'scale-transition' },
    transitionOrigin: { type: String, default: 'top left' },
  },

  data() {
    return {
      viewModel: null,
      menu: false,
      selectedOptionId: this.defaultOptionId,
    }
  },
  computed: {
    model: {
      get() {
        return this.getViewModel(this.viewModel)
      },
      set(newValue) {
        if (Array.isArray(newValue)) {
          // if its a range
          if (newValue.length === 2 && !this.multiple) {
            let vm = newValue

            if (daysDifference(vm[0], vm[1]) < 0) vm = newValue.reverse()

            // has Max Days
            if (this.maxDays > 0 && daysDifference(vm[0], vm[1]) > this.maxDays)
              vm[1] = getDateGivenOffset(vm[0], this.maxDays)

            this.viewModel = vm
          } else this.viewModel = newValue
        } else {
          this.viewModel = newValue
        }
      },
    },
    getThemeColor() {
      return this.color || variables.accent400
    },
    getLabelColor() {
      return variables.accent700
    },
    getContentClass() {
      return `asc-datepicker--menu ${this.landscape ? 'landscape' : ''}`
    },
    getDateInputFieldClass() {
      return [
        'date-picker-field',
        this.menu ? 'v-input-active-picker' : '',
        this.readonly ? 'v-input--is-nopick' : '',
        this.required ? 'is__required' : '',
        this.floatFilter ? 'v-input--is-float-filter' : '',
      ]
    },
    getSelectedOption() {
      return (
        this.getFixedDatePickerOptions.find(
          i => i.id === this.selectedOptionId,
        ) ||
        this.getCustomDatePickerOptions.find(
          i => i.id === this.selectedOptionId,
        )
      )
    },
    getIconPosition() {
      return this.dropDown ? 'right' : this.iconPosition
    },
    getHasIcon() {
      return this.dropDown ? true : this.hasIcon
    },
    getIconSize() {
      return this.dropDown ? 24 : this.iconSize
    },
    getIcon() {
      return this.dropDown ? 'mdi-chevron-down' : this.icon
    },
    getMax() {
      return this.max
        ? this.max instanceof Date
          ? this.formatDateToAPIDateString(this.max)
          : this.max
        : this.allowFutureDates
        ? undefined
        : this.formatDateToAPIDateString(new Date())
    },
    getMin() {
      return this.min instanceof Date
        ? this.formatDateToAPIDateString(this.min)
        : this.min
    },
    isFixedOptionChecked() {
      if (this.showFixedOptions && this.getSelectedOption)
        return this.getFixedDatePickerOptions.includes(this.getSelectedOption)

      return false
    },
    getFixedDatePickerOptions() {
      const options = this.fixedOptions
      if (this.hideOptionsWithId)
        return options.filter(o => this.hideOptionsWithId.indexOf(o.id) === -1)
      return options
    },
    getCustomDatePickerOptions() {
      const options = this.customOptions
      if (this.hideOptionsWithId)
        return options.filter(o => this.hideOptionsWithId.indexOf(o.id) === -1)
      return options
    },
    formatedDate() {
      if (this.floatFilter) {
        // For float filter the default option is 0 which is all dates
        // Dont show any value for default float filter
        if (this.getSelectedOption && this.getSelectedOption.value === '0') {
          return ''
        }
      }
      if (this.isFixedOptionChecked) {
        return this.getSelectedOption ? this.getSelectedOption.label : ''
      } else if (Array.isArray(this.model)) {
        const dates = []
        this.model.forEach(date =>
          date ? dates.push(this.formatDate(date, this.format)) : '',
        )
        return dates.length ? dates.join(' - ') : ''
      }

      return this.model ? this.formatDate(this.model, this.format) : ''
    },
    isRangePicker() {
      return (
        this.rangeOnly ||
        (this.showFixedOptions &&
          this.getSelectedOption &&
          this.getSelectedOption.type === 'range')
      )
    },
  },

  watch: {
    value: {
      deep: true,
      handler(newValue) {
        this.viewModel = this.getViewModel(newValue)
      },
    },
    isFixedOptionChecked(val) {
      if (!val && this.autoRePosition && this.showFixedOptions) {
        this.$refs[`asc-datepicker-menu-${this._uid}`].updateDimensions()
      }
    },
  },

  mounted() {
    if (this.value) {
      this.viewModel = this.getViewModel(this.value)
    }

    if (this.isFixedOptionChecked) {
      this.handleOptionSelection()
    }
  },

  methods: {
    formatDate(date, format = constants.LONG_DATE_FORMAT) {
      return moment.utc(date).format(format)
    },
    formatDateToAPIDateString(date) {
      return moment.utc(date).format(constants.API_DATE_FORMAT)
    },
    getOffsetDaysFromToday(offset = 0) {
      return this.formatDateToAPIDateString(
        getDateGivenOffset(Date.now(), offset),
      )
    },
    handleOptionSelection() {
      const selectedOption = this.getSelectedOption
      if (this.isFixedOptionChecked) {
        if (selectedOption) {
          if (selectedOption.type === 'date') {
            this.handleDatePick(
              this.getOffsetDaysFromToday(selectedOption.offset),
            )
          } else if (selectedOption.type === 'range') {
            this.handleDatePick([
              this.getOffsetDaysFromToday(selectedOption.offset),
              this.getOffsetDaysFromToday(),
            ])
          } else {
            // Reset
            this.handleDatePick()
          }
        }
      } else {
        // If custom date then reset
        if (selectedOption) {
          if (
            selectedOption.type === 'date' ||
            selectedOption.type === 'range'
          ) {
            this.handleDatePick()
          }
        }
      }
    },
    handleDatePick(date = null) {
      let selectedDate = null

      if (date) {
        // If date is selected then hide the date picker
        this.menu = false

        // Multiple or range picker
        if (Array.isArray(date)) {
          const dates = []
          date.forEach(date => dates.push(this.formatDateToAPIDateString(date)))

          // If its a range picker with start and end dates
          if (this.isRangePicker && !this.multiple && dates.length === 2) {
            selectedDate = {
              startDate: dates[0],
              endDate: dates[1],
              value: this.getSelectedOption
                ? this.getSelectedOption.value
                : null,
            }
          }

          // If its multiple dates prop enabled
          if (this.multiple) {
            selectedDate = {
              dates: dates,
            }
          }
        }
        // If its a single date picker
        else {
          selectedDate = {
            startDate: this.formatDateToAPIDateString(date),
            endDate: this.formatDateToAPIDateString(date),
            value: this.getSelectedOption ? this.getSelectedOption.value : null,
          }
        }
      }
      // If all dates is selected or null
      else {
        selectedDate = {
          startDate: null,
          endDate: null,
          value: this.getSelectedOption ? this.getSelectedOption.value : null,
        }

        if (this.getSelectedOption.type === 'date') {
          // If date calendar picker option is selected then default it to todays date apart from customDate option
          selectedDate.startDate =
            selectedDate.value === '5' ? null : this.getOffsetDaysFromToday()
          selectedDate.endDate =
            selectedDate.value === '5' ? null : this.getOffsetDaysFromToday()
          selectedDate.autoSelected = true
          this.menu = true
        } else if (this.getSelectedOption.type === 'range') {
          // If date range option is selected then default set to empty
          selectedDate.startDate = ''
          selectedDate.endDate = ''
        }
      }

      const hasDates = this.value
        ? Object.prototype.hasOwnProperty.call(this.value, 'dates')
        : false
      const hasStartDate = this.value
        ? Object.prototype.hasOwnProperty.call(this.value, 'startDate')
        : false
      const hasEndDate = this.value
        ? Object.prototype.hasOwnProperty.call(this.value, 'endDate')
        : false
      const hasDatesStartDate =
        this.value && this.value.dates
          ? Object.prototype.hasOwnProperty.call(this.value.dates, 'startDate')
          : false
      const hasDatesEndDate =
        this.value && this.value.dates
          ? Object.prototype.hasOwnProperty.call(this.value.dates, 'endDate')
          : false

      let selected = null
      if (hasDates && (hasDatesStartDate || hasDatesEndDate)) {
        selected = { dates: selectedDate }
      } else if (
        typeof this.value === 'object' &&
        this.value !== null &&
        (hasStartDate || hasEndDate)
      ) {
        selected = selectedDate
      } else if (typeof this.value === 'string' || this.value instanceof Date) {
        selected = this.formatDateToAPIDateString(date)
      } else if (selectedDate) {
        selected = { dates: selectedDate }
      } else {
        selected = date
      }

      this.$emit('input', selected)
      this.$emit('update:value', selected)

      // Date picker has ID
      if (this.id) {
        this.$emit('input:id', this.id, selected)
      }
    },
    getViewModel(dates) {
      if (dates) {
        const value = Object.prototype.hasOwnProperty.call(dates, 'dates')
          ? dates.dates
          : dates

        if (value) {
          // Value is an array then return
          if (Array.isArray(value)) {
            return value
          }

          // value is a object : Value is Object with start and end date
          const hasStartDate = Object.prototype.hasOwnProperty.call(
            value,
            'startDate',
          )

          const hasEndDate = Object.prototype.hasOwnProperty.call(
            value,
            'endDate',
          )

          const hasValue = Object.prototype.hasOwnProperty.call(value, 'value')
          let selectedOptionByValue = null

          if (hasValue) {
            // if the value has a value then select option from the value string
            const fixedOptions = this.fixedOptions.find(
              option => option.value === value.value,
            )

            const customOptions = this.customOptions.find(
              option => option.value === value.value,
            )

            selectedOptionByValue = fixedOptions || customOptions

            if (selectedOptionByValue) {
              this.selectedOptionId = selectedOptionByValue.id

              if (!this.selectedOptionId) {
                if (fixedOptions) {
                  this.handleOptionSelection()
                } else {
                  if (selectedOptionByValue.type === 'date') {
                    return value.startDate
                  } else if (selectedOptionByValue.type === 'range') {
                    return [value.startDate, value.endDate]
                  }
                }
              }
            }
          }

          if (this.isRangePicker) {
            return hasStartDate && hasEndDate
              ? value.startDate instanceof Date && value.endDate instanceof Date
                ? [
                    this.formatDateToAPIDateString(value.startDate),
                    this.formatDateToAPIDateString(value.endDate),
                  ]
                : [value.startDate, value.endDate]
              : hasStartDate
              ? value.startDate instanceof Date
                ? [this.formatDateToAPIDateString(value.startDate)]
                : [value.startDate]
              : hasValue && selectedOptionByValue
              ? [
                  this.getOffsetDaysFromToday(selectedOptionByValue.offset),
                  this.getOffsetDaysFromToday(),
                ]
              : [null, null]
          } else {
            return hasStartDate
              ? value.startDate instanceof Date
                ? this.formatDateToAPIDateString(value.startDate)
                : value.startDate
              : hasValue && selectedOptionByValue
              ? this.getOffsetDaysFromToday(selectedOptionByValue.offset)
              : value instanceof Date
              ? this.formatDateToAPIDateString(value)
              : value
          }
        }
      }

      return dates
    },
  },
}
</script>
